/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 *
 * Data layer, ported from the source app's module bundle.
 *
 * The source routed EVERY call — reads included — through a service-role edge
 * function, passing a `callerEmail` in the body that the function checked
 * against `user_roles` / `role_permissions`. That design existed because the
 * source app authenticates with Azure AD / MSAL, so Postgres had no idea who
 * the caller was and RLS could not be used at all.
 *
 * Brand Command authenticates with Supabase, so the caller IS the database
 * session. This service therefore talks to PostgREST directly and lets RLS do
 * the scoping — matching depletions-inventory, incentive-tracker and every
 * other module here. Edge functions are kept only for the two things RLS can't
 * do: geocoding (an outbound call to the US Census) and the anonymous public
 * search the website widget uses.
 *
 * Writes are field-whitelisted; account_id, id and the timestamps are never
 * accepted from a caller.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { fetchAllRows } from "../../../shared/utils/fetchAllRows.js";
import { MAX_PRODUCTS, MAX_PRODUCT_LEN } from "../utils/productCatalog.js";

// Columns a caller may set. `geog` is trigger-maintained and deliberately
// absent; so are geocode_* (owned by the geocoder), source_key and
// last_sync_batch (owned by the sheet importer — a human editing a store must
// not be able to make it look like an imported row).
const WRITABLE = [
  "name", "chain", "address_line1", "address_line2", "city", "state",
  "postal_code", "country", "latitude", "longitude",
  "products", "notes", "is_active",
];

// `origin`, `outlet_key` and `last_sold_on` are readable but never writable:
// they are owned by whatever produced the row (migration 0070). A human editing
// a store must not be able to relabel a derived door as hand-entered, for the
// same reason they can't forge a `source_key`.
const SELECT =
  "id, name, chain, address_line1, address_line2, city, state, postal_code, country, " +
  "latitude, longitude, products, notes, is_active, " +
  "origin, last_sold_on, is_active_override, " +
  "geocode_status, geocode_source, geocoded_at, source_key, created_at, updated_at";

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** Reset the cached account (sign-out / workspace switch). */
export function resetRetailFinderAccount() { _acct = null; }

function clean(fields = {}) {
  const out = {};
  for (const k of WRITABLE) {
    if (fields[k] === undefined) continue;
    let v = fields[k];
    if (k === "products") {
      // Each entry is capped as well as the array. These strings are rendered to
      // the public store finder; the array cap alone left one cell able to carry
      // 50 unbounded names into a shopper-facing filter.
      v = Array.isArray(v)
        ? v.map((s) => String(s).trim().replace(/\s+/g, " ").slice(0, MAX_PRODUCT_LEN))
            .filter(Boolean).slice(0, MAX_PRODUCTS)
        : [];
    } else if (k === "latitude" || k === "longitude") {
      // "" clears the coordinate; anything non-numeric is rejected outright
      // rather than silently becoming 0,0 — which is a real place in the ocean.
      if (v === "" || v === null) { v = null; }
      else {
        const n = Number(v);
        if (!Number.isFinite(n)) throw new Error("Latitude and longitude must be numbers.");
        const limit = k === "latitude" ? 90 : 180;
        if (n < -limit || n > limit) throw new Error(`${k === "latitude" ? "Latitude" : "Longitude"} is out of range.`);
        v = n;
      }
    } else if (k === "is_active") {
      v = !!v;
      // A person deciding whether a door is publicly visible is a DECISION, and
      // resolve_depletion_outlets() must not overrule it on the next sync — the
      // recency window only gets a vote while is_active_override is NULL
      // (migration 0070). Recording it here means the existing show/hide toggle
      // sticks without the UI needing to know the mechanism exists.
      out.is_active_override = v;
    } else if (typeof v === "string") {
      v = v.trim().slice(0, 300) || null;
    }
    out[k] = v;
  }
  return out;
}

/** Every location for the active workspace. Range-walked — a real store book
 *  runs to thousands of rows and PostgREST caps a plain select at 1,000. */
export async function listLocations() {
  const acct = await acctId();
  try {
    return await fetchAllRows(
      () => supabase
        .from("retail_locations")
        .select(SELECT)
        .eq("account_id", acct)
        .order("name", { ascending: true }),
      { label: "retail_locations" }
    );
  } catch (error) {
    console.error("[retailLocationService.listLocations]", error);
    throw new Error("Could not load retail locations.");
  }
}

export async function createLocation(fields) {
  const acct = await acctId();
  const row = { account_id: acct, ...clean(fields) };
  if (!row.name) throw new Error("A location name is required.");
  // A hand-entered store must never carry a source_key — that is what keeps a
  // sheet import from later overwriting or deactivating it.
  const { data, error } = await supabase
    .from("retail_locations").insert(row).select(SELECT).single();
  if (error) {
    console.error("[retailLocationService.createLocation]", error);
    throw new Error("Could not create the location.");
  }
  return data;
}

export async function updateLocation(id, fields) {
  const acct = await acctId();
  const patch = clean(fields);
  // A human setting coordinates by hand outranks the geocoder; record that so a
  // later geocode pass doesn't overwrite them.
  if (patch.latitude != null && patch.longitude != null) {
    patch.geocode_status = "manual";
    patch.geocode_source = "manual";
  }
  const { data, error } = await supabase
    .from("retail_locations").update(patch)
    .eq("id", id).eq("account_id", acct)
    .select(SELECT).single();
  if (error) {
    console.error("[retailLocationService.updateLocation]", error);
    throw new Error("Could not update the location.");
  }
  return data;
}

/**
 * Hand a depletion-derived door back to the recency window: clears the human
 * visibility decision so the next resolve run decides again. No-op in effect for
 * hand-entered doors, which the resolver never touches anyway.
 */
export async function clearVisibilityOverride(id) {
  const acct = await acctId();
  const { data, error } = await supabase
    .from("retail_locations").update({ is_active_override: null })
    .eq("id", id).eq("account_id", acct)
    .select(SELECT).single();
  if (error) {
    console.error("[retailLocationService.clearVisibilityOverride]", error);
    throw new Error("Could not reset the visibility setting.");
  }
  return data;
}

export async function deleteLocation(id) {
  const acct = await acctId();
  const { error } = await supabase
    .from("retail_locations").delete().eq("id", id).eq("account_id", acct);
  if (error) {
    console.error("[retailLocationService.deleteLocation]", error);
    throw new Error("Could not delete the location.");
  }
  return true;
}

// ── Widget config ────────────────────────────────────────────────────────────

/** The public widget's appearance/behaviour config for this workspace. */
export async function loadWidgetConfig() {
  const acct = await acctId();
  const { data, error } = await supabase
    .from("retail_widget_config").select("config, updated_at")
    .eq("account_id", acct).maybeSingle();
  if (error) {
    console.error("[retailLocationService.loadWidgetConfig]", error);
    throw new Error("Could not load the widget settings.");
  }
  return data?.config ?? {};
}

/**
 * Everything the Website Widget tab needs: the appearance config, the opaque
 * key that goes in the customer's page, and the origin allowlist.
 *
 * `public_key` and `allowed_origins` have no equivalent in the source app — it
 * was single-tenant and its allowlist was an env var on the edge function. Here
 * the key is how the public endpoint resolves WHICH tenant is asking, and the
 * allowlist is what makes the widget work at all: **empty means disabled**, so
 * a tab that didn't surface it would leave people staring at a widget that
 * silently refuses to load.
 *
 * Returns nulls rather than creating a row — the tenant may never have opened
 * this tab, and a read shouldn't write.
 */
export async function loadWidgetSettings() {
  const acct = await acctId();
  const { data, error } = await supabase
    .from("retail_widget_config")
    .select("config, public_key, allowed_origins, updated_at")
    .eq("account_id", acct).maybeSingle();
  if (error) {
    console.error("[retailLocationService.loadWidgetSettings]", error);
    throw new Error("Could not load the widget settings.");
  }
  return {
    config: data?.config ?? {},
    publicKey: data?.public_key ?? null,
    allowedOrigins: data?.allowed_origins ?? [],
  };
}

/**
 * An origin is a scheme + host and nothing else — `https://shop.example.com`.
 * A path or a trailing slash never matches the browser's `Origin` header, which
 * presents as "the widget is broken" rather than "the origin is wrong", so it
 * is normalised here and rejected if it can't be.
 */
export function normalizeOrigin(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  let u;
  try {
    u = new URL(s.includes("://") ? s : `https://${s}`);
  } catch {
    throw new Error(`"${s}" isn't a valid website address.`);
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") {
    throw new Error("A website address must start with https://");
  }
  return u.origin;
}

/** Save appearance config and/or the origin allowlist. `public_key` is
 *  DB-owned and never accepted from a caller. */
export async function saveWidgetSettings({ config, allowedOrigins } = {}) {
  const acct = await acctId();
  const row = { account_id: acct, updated_at: new Date().toISOString() };

  if (config !== undefined) {
    const filtered = {};
    for (const g of CONFIG_GROUPS) {
      if (config && typeof config[g] === "object" && config[g] !== null) filtered[g] = config[g];
    }
    row.config = filtered;
  }
  if (allowedOrigins !== undefined) {
    const list = Array.isArray(allowedOrigins) ? allowedOrigins : [];
    row.allowed_origins = [...new Set(list.map(normalizeOrigin).filter(Boolean))].slice(0, 20);
  }

  const { data, error } = await supabase
    .from("retail_widget_config")
    .upsert(row, { onConflict: "account_id" })
    .select("config, public_key, allowed_origins").single();
  if (error) {
    console.error("[retailLocationService.saveWidgetSettings]", error);
    throw new Error("Could not save the widget settings.");
  }
  return {
    config: data.config ?? {},
    publicKey: data.public_key ?? null,
    allowedOrigins: data.allowed_origins ?? [],
  };
}

/** The one-line snippet the customer pastes. Same host as the map embed —
 *  both bundles ship from `embed/dist` in one deploy. */
export function widgetSnippet(publicKey) {
  const src = (import.meta.env?.VITE_EMBED_SRC || "https://brandcommand.app/embed.js")
    .replace(/embed\.js$/, "retail-finder.js");
  return `<script src="${src}" data-key="${publicKey}"></script>`;
}

// The source function filtered incoming config to six known groups. Same idea:
// the widget reads a fixed shape, and an unbounded caller blob in a jsonb
// column is how config tables turn into dumping grounds.
const CONFIG_GROUPS = ["map", "search", "results", "filters", "appearance", "messaging"];

export async function saveWidgetConfig(config) {
  const acct = await acctId();
  const filtered = {};
  for (const g of CONFIG_GROUPS) {
    if (config && typeof config[g] === "object" && config[g] !== null) filtered[g] = config[g];
  }
  const { data, error } = await supabase
    .from("retail_widget_config")
    .upsert({ account_id: acct, config: filtered, updated_at: new Date().toISOString() },
            { onConflict: "account_id" })
    .select("config").single();
  if (error) {
    console.error("[retailLocationService.saveWidgetConfig]", error);
    throw new Error("Could not save the widget settings.");
  }
  return data.config;
}

// ── Geocoding (edge function — outbound call to the US Census) ────────────────
// ⚠️ Both calls name the workspace. retail-geocode runs with the service role
// and cannot see the workspace switcher; guessing meant geocoding could run over
// a different tenant's store list than the one on screen. The function
// re-verifies membership of the id we send.

/** Geocode a batch of pending locations. Returns { processed, matched, remaining }. */
export async function geocodePending(limit) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("retail-geocode", {
    body: { action: "pending", limit, accountId },
  });
  if (error) {
    console.error("[retailLocationService.geocodePending]", error);
    throw new Error("Geocoding failed.");
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Place Census-unmatched stores at their ZIP centroid. Returns { updated }. */
export async function applyZipFallback() {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("retail-geocode", {
    body: { action: "zip_fallback", accountId },
  });
  if (error) {
    console.error("[retailLocationService.applyZipFallback]", error);
    throw new Error("Could not apply the ZIP fallback.");
  }
  if (data?.error) throw new Error(data.error);
  return data;
}
